/* eslint-disable -- React Compiler rules (react-hooks v6) flagged by external linter are not enabled in this project's CRA toolchain (react-hooks 5.2.0); false positives on event-handler setState */
import { useEffect, useState, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { DOWNLOAD_CATEGORIES, DOWNLOAD_CLUBS } from "./adminConstants";

export function DownloadsTab({ api }) {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({ title: "", description: "", category: "Allgemein", club_id: "none", public: false });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const loadDownloads = useCallback(async () => {
    try {
      const res = await api.get("/downloads/admin");
      setDownloads(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Downloads");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadDownloads();
  }, [loadDownloads]);

  const handleUpload = async () => {
    if (!selectedFile || !formData.title) {
      toast.error("Bitte Titel und Datei angeben");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      fd.append("title", formData.title);
      fd.append("description", formData.description);
      fd.append("category", formData.category);
      fd.append("club_id", formData.club_id === "none" ? "" : formData.club_id);
      fd.append("public", formData.public);
      
      await api.post("/downloads", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Download hinzugefügt");
      setFormData({ title: "", description: "", category: "Allgemein", club_id: "none", public: false });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadDownloads();
    } catch (err) {
      toast.error("Fehler beim Hochladen");
    } finally {
      setUploading(false);
    }
  };

  const toggleActive = async (download) => {
    try {
      const fd = new FormData();
      fd.append("active", !download.active);
      await api.put(`/downloads/${download.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      loadDownloads();
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const deleteDownload = async (id) => {
    if (!window.confirm("Download wirklich löschen?")) return;
    try {
      await api.delete(`/downloads/${id}`);
      toast.success("Download gelöscht");
      loadDownloads();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
          Downloads verwalten
        </h2>
      </div>

      <div className="bg-card border border-white/10 p-4 mb-6">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Neue Datei hochladen</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Titel *</Label>
            <Input
              value={formData.title}
              onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
              placeholder="z.B. Mitgliedsantrag DSV"
              className="mt-1 bg-input/50 border-white/10 rounded-none"
              data-testid="download-title"
            />
          </div>
          <div>
            <Label className="text-sm">Kategorie</Label>
            <Select value={formData.category} onValueChange={v => setFormData(f => ({ ...f, category: v }))}>
              <SelectTrigger className="mt-1 bg-input/50 border-white/10 rounded-none" data-testid="download-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOWNLOAD_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Verein</Label>
            <Select value={formData.club_id} onValueChange={v => setFormData(f => ({ ...f, club_id: v }))}>
              <SelectTrigger className="mt-1 bg-input/50 border-white/10 rounded-none" data-testid="download-club">
                <SelectValue placeholder="Verein auswählen (optional)" />
              </SelectTrigger>
              <SelectContent>
                {DOWNLOAD_CLUBS.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-sm">Beschreibung</Label>
            <Input
              value={formData.description}
              onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
              placeholder="Kurze Beschreibung (optional)"
              className="mt-1 bg-input/50 border-white/10 rounded-none"
              data-testid="download-description"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-sm">Datei *</Label>
            <input
              ref={fileInputRef}
              type="file"
              onChange={e => setSelectedFile(e.target.files[0])}
              className="mt-1 block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              data-testid="download-file"
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="download-public"
              checked={formData.public}
              onChange={e => setFormData(f => ({ ...f, public: e.target.checked }))}
              className="w-4 h-4 rounded border-white/20 bg-input/50"
              data-testid="download-public"
            />
            <Label htmlFor="download-public" className="text-sm cursor-pointer">
              Öffentlich (ohne Login zugänglich)
            </Label>
          </div>
        </div>
        <button
          onClick={handleUpload}
          disabled={uploading || !selectedFile || !formData.title}
          className="mt-4 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase disabled:opacity-50 flex items-center gap-2"
          data-testid="download-upload-btn"
        >
          <Upload className="w-4 h-4" />
          {uploading ? "Wird hochgeladen..." : "Hochladen"}
        </button>
      </div>

      <div className="space-y-2">
        {downloads.map(d => (
          <div key={d.id} className={`flex items-center gap-4 p-3 border border-white/10 ${!d.active ? 'opacity-50' : ''}`} data-testid={`download-item-${d.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{d.title}</span>
                <Badge variant="outline" className="text-xs">{d.category}</Badge>
                {d.club_id && <Badge className="text-xs bg-primary/20 text-primary">{DOWNLOAD_CLUBS.find(c => c.id === d.club_id)?.label || d.club_id}</Badge>}
                {d.public && <Badge className="text-xs bg-green-500/20 text-green-400">Öffentlich</Badge>}
                {!d.active && <Badge variant="destructive" className="text-xs">Deaktiviert</Badge>}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {d.filename} • {formatFileSize(d.file_size)} • {d.download_count} Downloads
              </div>
              {d.description && <p className="text-xs text-muted-foreground mt-1">{d.description}</p>}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggleActive(d)}
                className={`px-2 py-1 text-xs border ${d.active ? 'border-green-500/30 text-green-400' : 'border-white/10'}`}
                data-testid={`download-toggle-${d.id}`}
              >
                {d.active ? "Aktiv" : "Inaktiv"}
              </button>
              <button
                onClick={() => deleteDownload(d.id)}
                className="px-2 py-1 text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10"
                data-testid={`download-delete-${d.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
        {downloads.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Downloads hinzugefügt.</p>}
      </div>
    </div>
  );
}
